import type { InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { orpc } from "@shared/lib/orpc";
import { z } from "zod";

type Outputs = InferRouterOutputs<ApiRouter>["logs"];
export type LogRow = Outputs["list"]["logs"][number];
export type LogDetail = Outputs["get"];
export type LogSource = LogRow["source"];
export type LogOutcome = LogRow["outcome"];
export type ErrorGroup = Outputs["errors"]["groups"][number];

/** The source tabs, in the order an admin reads them (spec 5.6). */
export const SOURCE_TABS = [
	"WEBHOOK_IN",
	"GRAPH_OUT",
	"FLOW_ENDPOINT",
	"CALL_SIGNAL",
	"ORDER_ADAPTER",
	"ACCOUNT",
] as const satisfies readonly LogSource[];

export const SOURCE_LABEL: Record<LogSource, string> = {
	WEBHOOK_IN: "Webhook in",
	GRAPH_OUT: "Sent to Meta",
	FLOW_ENDPOINT: "Checkout form",
	CALL_SIGNAL: "Calls",
	ORDER_ADAPTER: "Order system",
	ACCOUNT: "Account",
	HEALTH_CHECK: "Health check",
};

/** Plural tab names: "Webhooks in" reads as a place, a row says "Webhook in". */
export const SOURCE_TAB_LABEL: Record<(typeof SOURCE_TABS)[number], string> = {
	WEBHOOK_IN: "Webhooks in",
	GRAPH_OUT: "Sent to Meta",
	FLOW_ENDPOINT: "Checkout form",
	CALL_SIGNAL: "Calls",
	ORDER_ADAPTER: "Order system",
	ACCOUNT: "Account",
};

export const OUTCOMES = [
	"FAILED",
	"OK",
	"IGNORED",
	"DUPLICATE",
] as const satisfies readonly LogOutcome[];

export const OUTCOME_META: Record<
	LogOutcome,
	{
		label: string;
		variant: "success" | "destructive" | "secondary" | "outline";
	}
> = {
	OK: { label: "Succeeded", variant: "success" },
	FAILED: { label: "Failed", variant: "destructive" },
	IGNORED: { label: "Ignored", variant: "secondary" },
	DUPLICATE: { label: "Duplicate", variant: "outline" },
};

/** The errors strip's windows. */
export const WINDOWS = [1, 24, 24 * 7, 24 * 30] as const;
export const WINDOW_LABEL: Record<(typeof WINDOWS)[number], string> = {
	1: "Last hour",
	24: "Last 24 hours",
	168: "Last 7 days",
	720: "Last 30 days",
};

/** Every filter is in the URL, so "why didn't Rami get his list" is a link (DESIGN.md 11). */
export const logsSearchSchema = z.object({
	source: z.enum(SOURCE_TABS).optional(),
	outcome: z.enum(OUTCOMES).optional(),
	/** A WhatsApp number's connection id. */
	number: z.string().optional(),
	/** A Meta error code from the errors strip. */
	code: z.coerce.number().int().optional(),
	/** A wamid, call id, order id or conversation id. */
	q: z.coerce.string().optional(),
	/** The row open in the detail sheet. */
	id: z.string().optional(),
	hours: z.coerce
		.number()
		.refine((value) => (WINDOWS as readonly number[]).includes(value))
		.optional(),
	/** Poll every 5 s. */
	live: z.boolean().optional(),
});
export type LogsSearch = z.infer<typeof logsSearchSchema>;

export const LOGS_PAGE = 50;
export const LIVE_POLL_MS = 5_000;

export function logsQuery(
	organizationId: string,
	search: Pick<LogsSearch, "source" | "outcome" | "number" | "code" | "q">,
) {
	return orpc.logs.list.infiniteOptions({
		input: (cursor: string | undefined) => ({
			organizationId,
			limit: LOGS_PAGE,
			...(search.source ? { source: search.source } : {}),
			...(search.outcome ? { outcome: search.outcome } : {}),
			...(search.number ? { connectionId: search.number } : {}),
			...(search.code !== undefined ? { errorCode: search.code } : {}),
			...(search.q?.trim() ? { q: search.q.trim() } : {}),
			...(cursor ? { cursor } : {}),
		}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
	});
}

export function logErrorsQuery(
	organizationId: string,
	hours: number,
	connectionId?: string,
) {
	return orpc.logs.errors.queryOptions({
		input: {
			organizationId,
			hours,
			...(connectionId ? { connectionId } : {}),
		},
	});
}

/**
 * Consecutive rows about one message, call or order collapse into one line (a send, its
 * statuses, a retry), newest first; the sheet shows each step on its timeline.
 */
export function groupRows(rows: readonly LogRow[]): LogRow[][] {
	const groups: LogRow[][] = [];
	for (const row of rows) {
		const key = requestKey(row);
		const last = groups.at(-1);
		const lastKey = last?.[0] ? requestKey(last[0]) : null;
		if (last && key && key === lastKey) {
			last.push(row);
		} else {
			groups.push([row]);
		}
	}
	return groups;
}

function requestKey(row: LogRow): string | null {
	return row.wamid
		? `m:${row.wamid}`
		: row.callId
			? `c:${row.callId}`
			: row.orderId
				? `o:${row.orderId}`
				: null;
}

/** "200 · 412 ms", "Failed · 131026", "Ignored". */
export function resultOf(row: LogRow): string {
	const parts = [
		row.httpStatus ? String(row.httpStatus) : null,
		row.errorCode ? String(row.errorCode) : null,
		row.durationMs != null ? formatDuration(row.durationMs) : null,
	].filter(Boolean);
	return parts.length ? parts.join(" · ") : OUTCOME_META[row.outcome].label;
}

export function formatDuration(ms: number): string {
	return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}
